import React from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { td } from '@/lib/i18n';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, CheckCircle, Flag, Play, Plus, Users, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { TextField } from '@/components/text-field';
import { TextAreaField } from '@/components/textarea-field';
import { SelectField } from '@/components/select-field';
import { DateField } from '@/components/date-field';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { campaignAssignmentSchema } from '@/schema/campaign-assignment-schema';
import { activateCampaignAssignment, endCampaignAssignment, fetchCampaignAssignment, fetchCampaignAssignments, postCampaignAssignment, putCampaignAssignment, terminateCampaignAssignment, } from '@/services/campaign-assignment';
import { fetchDrivers } from '@/services/driver';
import { Checkbox } from '@/components/ui/checkbox';
import { errorHandler } from '@/lib/utils';
import { isInternalAdmin } from '@/lib/roles';
import { useAuthenticationState } from '@/hooks/use-authentication';
export const Route = createFileRoute('/console/_authenticated/_layout/internal/_layout/assignments/$publicId')({
    component: AssignmentDetailPage,
});
const STATUS_VARIANT = {
    scheduled: 'secondary',
    active: 'default',
    ended: 'outline',
    terminated: 'destructive',
};
const REASON_KEYS = [
    'completed',
    'driver_left',
    'campaign_cancelled',
    'rate_renegotiation',
    'other',
];
function AssignmentDetailPage() {
    const { t } = useTranslation('internal');
    const { publicId } = Route.useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { user } = useAuthenticationState();
    const canAdmin = isInternalAdmin(user);
    const { data: assignment, isLoading } = useQuery({
        queryKey: ['campaign-assignment', publicId],
        queryFn: () => fetchCampaignAssignment(publicId),
    });
    if (isLoading)
        return <p className="p-6 text-sm text-muted-foreground">{t('common.loading')}</p>;
    if (!assignment)
        return <p className="p-6 text-sm text-muted-foreground">—</p>;
    const onChanged = (updated) => {
        queryClient.setQueryData(['campaign-assignment', publicId], updated);
        queryClient.invalidateQueries({ queryKey: ['campaign-assignments'] });
    };
    const isTerminal = assignment.status === 'ended' || assignment.status === 'terminated';
    return (<div className="flex flex-col gap-6 p-6 max-w-3xl mx-auto w-full">
            <div className="flex items-center justify-between">
                <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/console/internal/assignments' })}>
                    <ArrowLeft className="h-4 w-4"/>
                    {t('assignments.page.back')}
                </Button>
                <Badge variant={STATUS_VARIANT[assignment.status]}>
                    {td(t, `enum.assignment_status.${assignment.status}`)}
                </Badge>
            </div>

            <div className="flex items-start gap-4">
                {assignment.driverVehicleImageDetailUrl ? (<img src={assignment.driverVehicleImageDetailUrl} alt="" loading="lazy" className="h-24 w-24 rounded-lg border object-cover shrink-0"/>) : (<div className="h-24 w-24 rounded-lg border bg-muted text-muted-foreground text-xl font-medium flex items-center justify-center shrink-0">
                        {assignment.driverName.slice(0, 2).toUpperCase()}
                    </div>)}
                <div className="space-y-1 min-w-0">
                    <h1 className="text-3xl font-semibold truncate">{assignment.driverName}</h1>
                    <p className="text-sm text-muted-foreground">
                        {assignment.campaignName} ·{' '}
                        {t('assignments.rate_line', {
            currency: assignment.currency,
            rate: assignment.monthlyRate,
            cycle: td(t, `enum.photo_cycle.${assignment.photoRequestCycle}`),
        })}
                    </p>
                    {assignment.endedAt && assignment.endReason && (<p className="text-xs text-muted-foreground">
                            {t('assignments.ended_line', {
                date: assignment.endedAt.slice(0, 10),
                reason: td(t, `enum.end_reason.${assignment.endReason}`),
            })}
                        </p>)}
                </div>
            </div>

            <AssignmentEditCard assignment={assignment} disabled={!canAdmin || isTerminal} onSaved={onChanged}/>

            <CampaignDriversCard assignment={assignment} allowAddDrivers={!isTerminal}/>

            {canAdmin && <AssignmentStatusCard assignment={assignment} onChanged={onChanged}/>}
        </div>);
}
function CampaignDriversCard({ assignment, allowAddDrivers, }) {
    const { t } = useTranslation('internal');
    const [addOpen, setAddOpen] = React.useState(false);
    const { data, isLoading, isError } = useQuery({
        queryKey: ['campaign-assignments'],
        queryFn: fetchCampaignAssignments,
        staleTime: 30_000,
    });
    const campaignPublicId = assignment.campaignPublicId;
    const currentAssignmentPublicId = assignment.publicId;
    // Filter client-side: every assignment for the same campaign, current one
    // included so staff can see "we're currently a 3-driver campaign".
    const drivers = (data ?? []).filter((a) => a.campaignPublicId === campaignPublicId);
    return (<Card>
            <CardHeader>
                <div className="flex items-start justify-between gap-2">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            <Users className="h-4 w-4"/>
                            {t('assignments.section.campaign_drivers')}
                        </CardTitle>
                        <CardDescription>{t('assignments.section.campaign_drivers_desc')}</CardDescription>
                    </div>
                    {allowAddDrivers && (<Button size="sm" variant="outline" onClick={() => setAddOpen(true)}>
                            <UserPlus className="h-4 w-4"/>
                            {t('assignments.action.add_another_driver')}
                        </Button>)}
                </div>
            </CardHeader>
            <CardContent className="space-y-2">
                {isLoading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
                {isError && <p className="text-sm text-destructive">{t('common.load_error')}</p>}
                {!isLoading && !isError && drivers.length === 0 && (<p className="text-sm text-muted-foreground">{t('assignments.section.campaign_drivers_none')}</p>)}
                {drivers.map((a) => {
            const isCurrent = a.publicId === currentAssignmentPublicId;
            return (<Link key={a.publicId} to="/console/internal/assignments/$publicId" params={{ publicId: a.publicId }} className={'block rounded-md border p-3 transition-colors ' +
                    (isCurrent ? 'bg-muted/40' : 'hover:bg-muted/50')}>
                            <div className="flex items-start justify-between gap-2">
                                <div className="space-y-0.5 min-w-0">
                                    <p className="text-sm font-medium truncate">
                                        {a.driverName}
                                        {isCurrent && (<span className="ml-2 text-xs text-muted-foreground">
                                                ({t('assignments.section.campaign_drivers_this')})
                                            </span>)}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {a.startDate.slice(0, 10)} –{' '}
                                        {a.endDate ? a.endDate.slice(0, 10) : t('drivers.section.assignments_open')}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <Badge variant={STATUS_VARIANT[a.status]}>
                                        {td(t, `enum.assignment_status.${a.status}`)}
                                    </Badge>
                                    {!isCurrent && <ArrowRight className="h-4 w-4 text-muted-foreground"/>}
                                </div>
                            </div>
                        </Link>);
        })}
            </CardContent>

            <AddDriversToCampaignDialog assignment={assignment} driversAlreadyOnCampaign={
        // Only OPEN assignments (active/scheduled) — a driver who
        // previously ended on this campaign is free to be re-added.
        new Set(drivers
            .filter((d) => d.status === 'active' || d.status === 'scheduled')
            .map((d) => d.driverPublicId))} open={addOpen} onOpenChange={setAddOpen}/>
        </Card>);
}
function AddDriversToCampaignDialog({ assignment, driversAlreadyOnCampaign, open, onOpenChange, }) {
    const { t } = useTranslation('internal');
    const queryClient = useQueryClient();
    const [selected, setSelected] = React.useState(new Set());
    const { data: drivers = [] } = useQuery({
        queryKey: ['drivers'],
        queryFn: fetchDrivers,
    });
    const { data: allAssignments = [] } = useQuery({
        queryKey: ['campaign-assignments'],
        queryFn: fetchCampaignAssignments,
    });
    // Drivers with any open (active/scheduled) assignment elsewhere can't carry
    // a second campaign — block them in the picker, just like the new-assignment
    // page does.
    const driversTakenElsewhere = React.useMemo(() => new Set(allAssignments
        .filter((a) => a.status === 'scheduled' || a.status === 'active')
        .map((a) => a.driverPublicId)), [allAssignments]);
    React.useEffect(() => {
        if (!open)
            setSelected(new Set());
    }, [open]);
    const { mutate, isPending } = useMutation({
        mutationFn: postCampaignAssignment,
        onSuccess: (created) => {
            toast.success(created.length > 1
                ? t('assignments.toast.bulk_created', { count: created.length })
                : t('assignments.toast.created'));
            queryClient.invalidateQueries({ queryKey: ['campaign-assignments'] });
            queryClient.invalidateQueries({ queryKey: ['driver-assignments'] });
            onOpenChange(false);
        },
        onError: errorHandler,
    });
    const submit = () => {
        if (selected.size === 0)
            return;
        mutate({
            driverPublicIds: Array.from(selected),
            campaignPublicId: assignment.campaignPublicId,
            startDate: assignment.startDate.slice(0, 10),
            endDate: assignment.endDate ? assignment.endDate.slice(0, 10) : undefined,
            monthlyRate: parseFloat(assignment.monthlyRate),
            currency: assignment.currency,
            photoRequestCycle: assignment.photoRequestCycle,
            rateCycle: assignment.rateCycle,
        });
    };
    const toggle = (publicId, next) => {
        setSelected((prev) => {
            const copy = new Set(prev);
            if (next)
                copy.add(publicId);
            else
                copy.delete(publicId);
            return copy;
        });
    };
    return (<Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{t('assignments.add_dialog.title')}</DialogTitle>
                    <DialogDescription>
                        {t('assignments.add_dialog.desc', {
            campaign: assignment.campaignName,
            currency: assignment.currency,
            rate: assignment.monthlyRate,
            cycle: td(t, `enum.rate_cycle.${assignment.rateCycle}`),
        })}
                    </DialogDescription>
                </DialogHeader>

                <div className="rounded-md border max-h-72 overflow-y-auto divide-y">
                    {drivers.length === 0 ? (<p className="p-3 text-sm text-muted-foreground">{t('assignments.field.drivers_empty')}</p>) : (drivers.map((driver) => {
            const onThisCampaign = driversAlreadyOnCampaign.has(driver.publicId);
            const taken = driversTakenElsewhere.has(driver.publicId) && !onThisCampaign;
            const blocked = onThisCampaign || taken;
            const checked = selected.has(driver.publicId);
            return (<label key={driver.publicId} className={'flex items-center justify-between gap-3 px-3 py-2.5 text-sm transition-colors ' +
                    (blocked ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted/40 cursor-pointer')}>
                                    <div className="flex items-center gap-3 min-w-0">
                                        <Checkbox checked={checked} disabled={blocked || isPending} onCheckedChange={(next) => toggle(driver.publicId, next === true)}/>
                                        {driver.vehicleImageUrl ? (<img src={driver.vehicleImageUrl} alt="" className="h-10 w-10 rounded object-cover border shrink-0" loading="lazy"/>) : (<div className="h-10 w-10 rounded border bg-muted text-xs flex items-center justify-center text-muted-foreground shrink-0">
                                                {driver.fullName.slice(0, 2).toUpperCase()}
                                            </div>)}
                                        <div className="min-w-0">
                                            <p className="font-medium truncate">{driver.fullName}</p>
                                            <p className="text-xs text-muted-foreground truncate">
                                                {driver.licensePlate}
                                            </p>
                                        </div>
                                    </div>
                                    {onThisCampaign && (<Badge variant="outline" className="shrink-0">
                                            {t('assignments.add_dialog.already_on_campaign')}
                                        </Badge>)}
                                    {!onThisCampaign && taken && (<Badge variant="outline" className="shrink-0">
                                            {t('assignments.field.driver_taken_suffix')}
                                        </Badge>)}
                                </label>);
        }))}
                </div>

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
                        {t('common.cancel')}
                    </Button>
                    <Button onClick={submit} disabled={selected.size === 0 || isPending}>
                        <Plus className="h-4 w-4"/>
                        {t('assignments.add_dialog.submit', { count: selected.size })}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>);
}
function AssignmentEditCard({ assignment, disabled, onSaved, }) {
    const { t } = useTranslation('internal');
    // The edit form keeps driverPublicIds populated so the schema's
    // `min(1)` rule passes; the backend ignores driver/campaign on PUT
    // because they're immutable on an existing assignment.
    const form = useForm({
        resolver: yupResolver(campaignAssignmentSchema),
        defaultValues: {
            driverPublicIds: [assignment.driverPublicId],
            campaignPublicId: assignment.campaignPublicId,
            startDate: assignment.startDate.slice(0, 10),
            endDate: assignment.endDate ? assignment.endDate.slice(0, 10) : '',
            monthlyRate: parseFloat(assignment.monthlyRate),
            currency: assignment.currency,
            photoRequestCycle: assignment.photoRequestCycle,
            rateCycle: assignment.rateCycle,
            notes: assignment.notes ?? '',
        },
    });
    const { mutate, isPending } = useMutation({
        mutationFn: (data) => putCampaignAssignment({ publicId: assignment.publicId, data }),
        onSuccess: (updated) => {
            toast.success(t('assignments.toast.updated'));
            onSaved(updated);
        },
        onError: errorHandler,
    });
    const cycleOptions = ['weekly', 'bi_weekly', 'monthly'].map((value) => ({
        value,
        label: td(t, `enum.photo_cycle.${value}`),
    }));
    const rateCycleOptions = ['weekly', 'bi_weekly', 'monthly'].map((value) => ({
        value,
        label: td(t, `enum.rate_cycle.${value}`),
    }));
    // Preserve unknown currencies (e.g. an older assignment created with GBP)
    // — without this, the Select would show blank for off-list values.
    const baseCurrencies = ['USD', 'KHR', 'EUR'];
    const currencyOptions = (baseCurrencies.includes(assignment.currency) ? baseCurrencies : [assignment.currency, ...baseCurrencies]).map((value) => ({ value, label: value }));
    return (<Form {...form}>
            <Card>
                <CardHeader>
                    <CardTitle>{t('assignments.section.edit')}</CardTitle>
                    <CardDescription>
                        {disabled
            ? t('assignments.section.edit_desc_readonly')
            : t('assignments.section.edit_desc_admin')}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                        <DateField control={form.control} name="startDate" label={t('assignments.field.start_date')} disabled={disabled || isPending}/>
                        <DateField control={form.control} name="endDate" label={t('assignments.field.end_date_optional')} disabled={disabled || isPending} nullable/>
                    </div>
                    <div className="grid gap-4 md:grid-cols-3">
                        <TextField control={form.control} name="monthlyRate" type="number" step="0.01" label={t('assignments.field.monthly_rate')} disabled={disabled || isPending}/>
                        <SelectField control={form.control} name="rateCycle" label={t('assignments.field.rate_cycle')} options={rateCycleOptions} disabled={disabled || isPending}/>
                        <SelectField control={form.control} name="currency" label={t('assignments.field.currency')} options={currencyOptions} disabled={disabled || isPending}/>
                    </div>
                    <SelectField control={form.control} name="photoRequestCycle" label={t('assignments.field.photo_cycle')} options={cycleOptions} disabled={disabled || isPending}/>
                    <TextAreaField control={form.control} name="notes" label={t('assignments.field.notes')} disabled={disabled || isPending}/>

                    <Button size="sm" onClick={form.handleSubmit((data) => mutate(data))} disabled={disabled || isPending}>
                        <CheckCircle className="h-4 w-4"/>
                        {t('common.save_changes')}
                    </Button>
                </CardContent>
            </Card>
        </Form>);
}
function AssignmentStatusCard({ assignment, onChanged, }) {
    const { t } = useTranslation('internal');
    const [closeOpen, setCloseOpen] = React.useState(false);
    const { mutate: doActivate, isPending: activating } = useMutation({
        mutationFn: () => activateCampaignAssignment(assignment.publicId),
        onSuccess: (updated) => {
            toast.success(t('assignments.toast.activated'));
            onChanged(updated);
        },
        onError: errorHandler,
    });
    return (<Card>
            <CardHeader>
                <CardTitle>{t('assignments.section.lifecycle')}</CardTitle>
                <CardDescription>{t('assignments.section.lifecycle_desc')}</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
                {assignment.status === 'scheduled' && (<Button size="sm" onClick={() => doActivate()} disabled={activating}>
                        <Play className="h-4 w-4"/>
                        {t('assignments.action.activate')}
                    </Button>)}
                {(assignment.status === 'scheduled' || assignment.status === 'active') && (<Button variant="outline" size="sm" onClick={() => setCloseOpen(true)}>
                        <Flag className="h-4 w-4"/>
                        {t('assignments.action.close')}
                    </Button>)}
            </CardContent>

            <CloseAssignmentDialog open={closeOpen} onOpenChange={setCloseOpen} publicId={assignment.publicId} onSuccess={(updated) => {
            setCloseOpen(false);
            onChanged(updated);
        }}/>
        </Card>);
}
function CloseAssignmentDialog({ open, onOpenChange, publicId, onSuccess, }) {
    const { t } = useTranslation('internal');
    const [reason, setReason] = React.useState('completed');
    const { mutate, isPending } = useMutation({
        mutationFn: () => reason === 'completed'
            ? endCampaignAssignment({ publicId, reason })
            : terminateCampaignAssignment({ publicId, reason }),
        onSuccess: (updated) => {
            toast.success(reason === 'completed' ? t('assignments.toast.ended') : t('assignments.toast.terminated'));
            onSuccess(updated);
        },
        onError: errorHandler,
    });
    return (<Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{t('assignments.close_dialog.title')}</DialogTitle>
                    <DialogDescription>{t('assignments.close_dialog.desc')}</DialogDescription>
                </DialogHeader>

                <div className="space-y-2">
                    <Label>{t('assignments.close_dialog.reason')}</Label>
                    <Select value={reason} onValueChange={(v) => setReason(v)}>
                        <SelectTrigger className="w-full">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {REASON_KEYS.map((value) => (<SelectItem key={value} value={value}>
                                    {td(t, `enum.end_reason.${value}`)}
                                </SelectItem>))}
                        </SelectContent>
                    </Select>
                </div>

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
                        {t('common.cancel')}
                    </Button>
                    <Button onClick={() => mutate()} disabled={isPending}>
                        {t('common.confirm')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>);
}
